import { FC, useEffect, useMemo, useState } from 'react'
import { getCurrency, iconPath } from '@ui/helpers'

import Countdown from '@ui/components/shared/Countdown'
import Variant from '@ui/components/shared/OrderVariants/Variant'
import css from 'styled-jsx/css'
import { useResize } from '@ui/hooks'
import { getPoStatus } from '@ui/services'
import TrackingVariant from '@ui/components/shared/OrderVariants/TrackingVariant'

interface IOrderVariant {
  order: Types.Order
  size?: 'small' | 'normal'
  isCheckingOut?: boolean
  title: string
  orderShopHelp?: Types.OrderShopHelp
}

const OrderVariantsTracking: FC<IOrderVariant> = ({
  order,
  size = 'normal',
  isCheckingOut,
  title,
  orderShopHelp
}: IOrderVariant) => {
  const label = 'Order Summary'
  const showSummary = true

  const {
    shipping_amount,
    items,
    shipping_code,
    discount_code,
    discount_amount,
    items_amount,
    amount,
    tax_value
  } = order

  const [cCollapsible, setcCollapsible] = useState<boolean | undefined>(true)
  const [cCollapsed, setcCollapsed] = useState<boolean | undefined>(true)

  const { payment, order_lines } = orderShopHelp ?? {}

  const poStatus = useMemo(() => orderShopHelp?.payment && getPoStatus(orderShopHelp.payment), [
    orderShopHelp
  ])

  function toogleCollape() {
    setcCollapsed(!cCollapsed)
  }

  function handleResize() {
    if (process.browser) {
      const windowWidth = window.innerWidth
      if (windowWidth > 768) {
        setcCollapsible(false)
        setcCollapsed(false)
      } else {
        setcCollapsible(true)
      }
    }
  }
  useResize(() => handleResize())

  useEffect(() => {
    handleResize()
  }, [])

  return (
    <>
      <style jsx>{OrderVariantStyle}</style>
      <div className="order-variants">
        <div className="collapse collapse__border">
          {!cCollapsible ? (
            <>
              <p className="collapse__title--bold">{label}</p>
              {!cCollapsible && poStatus && (
                <span className="collapse__status--container mt-1">
                  <span className="collapse__status--content">{poStatus}</span>
                </span>
              )}
            </>
          ) : (
            <a className="collapse__title" onClick={() => toogleCollape()}>
              <div className="collapse__title__text">
                <figure className="title__icon image">
                  <img src={iconPath('cart_grey.svg')} alt="" />
                </figure>
                <span className="title__text">{cCollapsed ? 'Show' : 'Hide'} order summary</span>
                <figure className="title__icon--right image">
                  <img
                    src={iconPath('chevron_down.svg')}
                    alt=""
                    style={!cCollapsed ? { display: 'none' } : {}}
                  />
                  <img
                    src={iconPath('chevron_up.svg')}
                    alt=""
                    style={cCollapsed ? { display: 'none' } : {}}
                  />
                </figure>
                {cCollapsed && !isCheckingOut ? (
                  <span className="title__net-payment">{getCurrency(amount ?? 0)}</span>
                ) : null}
              </div>
            </a>
          )}
        </div>
        {!cCollapsed ? (
          <div className="variant-wrapper">
            {order_lines
              ? order_lines.map((variant, index) => (
                  <div key={index} className="variant-item">
                    <TrackingVariant title={title} variant={variant} size={size} />
                    <hr />
                  </div>
                ))
              : items?.map((variant, index) => {
                  return (
                    <div key={index} className="variant-item">
                      <Variant title={title} variant={variant} size={size} />
                      <hr />
                    </div>
                  )
                })}
          </div>
        ) : null}
        {showSummary && (isCheckingOut || !cCollapsed) ? (
          <div className="order-variants__summary">
            {/* Subtotal */}
            <div className="item">
              <div className="item__label">Subtotal</div>
              <div className="item__number">{getCurrency(items_amount ?? 0)}</div>
            </div>

            {/* Shipping Code */}
            <div className="item">
              <div className="item__label">{shipping_code}</div>
              <div className="item__number">
                {shipping_amount ? getCurrency(shipping_amount ?? 0) : 'Free'}
              </div>
            </div>

            {/* Tax */}
            {(tax_value || tax_value == 0) && (
              <div className="item">
                <div className="item__label">{`Tax`}</div>
                <div className="item__number">{getCurrency(tax_value)}</div>
              </div>
            )}

            {discount_code ? (
              <div className="item">
                <div className="item__label">
                  Discount ({discount_code})
                  {isCheckingOut ? (
                    <span className="is-hidden-tablet">
                      - Expire in <Countdown template={2} className={CountdownStyle.className} />
                      {CountdownStyle.styles}
                    </span>
                  ) : null}
                </div>
                <div className="item__number">- {getCurrency(discount_amount ?? 0)}</div>
              </div>
            ) : null}
            <div className="item">
              <div className="item__label item__label--bold">Total</div>
              <div className="item__number item__number--bold">{getCurrency(amount ?? 0)}</div>
            </div>
            {!!payment?.refund_amount && payment.refund_amount > 0 && (
              <>
                <div className="item">
                  <div className="item__label item__label--refund">Refunded</div>
                  <div className="item__number item__number--refund">
                    - {getCurrency(payment.refund_amount)}
                  </div>
                </div>
                <div className="item">
                  <div className="item__label item__label--bold">Net Amount</div>
                  <div className="item__number item__number--bold">
                    {getCurrency(payment?.net_payment ?? 0)}
                  </div>
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
    </>
  )
}

export default OrderVariantsTracking

const OrderVariantStyle = css`
  .order-variants {
    @media screen and (min-width: 769px) {
      margin-bottom: 42px;
    }

    .collapse__border {
      border-top: 0 !important;
    }

    .collapse {
      display: flex;

      &__title {
        color: var(--text);
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 11px;
        padding-top: 10px;
        padding-bottom: 10px;

        &--bold {
          font-size: 20px;
          font-weight: bold;
          line-height: 30px;
          margin-bottom: 40px;
        }

        &__text {
          display: flex;
          align-items: center;

          .title {
            &__text {
              color: var(--text);
              font-size: 14px;
              line-height: 32px;
              margin-left: 10px;
              margin-right: 10px;
              opacity: 0.5;
            }

            &__icon {
              img {
                height: 20px;
                width: 20px;
              }

              &--right {
                img {
                  height: 19px;
                  width: 14px;
                }
              }
            }

            &__net-payment {
              font-weight: 600;
              font-size: 15px;
              line-height: 32px;
              position: absolute;
              right: 12px;
            }
          }
        }
      }

      &__status {
        &--container {
          margin-left: 20px;
          padding: 0;
          height: 28px;
          background: rgba(235, 87, 87, 0.1);
          border-left: 4px solid #eb5757;
        }

        &--content {
          font-size: 10px;
          line-height: 16px;
          margin: 6px 16px;
          color: #eb5757;
        }
      }

      @media screen and (max-width: 768px) {
        &__border {
          margin: -0.75rem;
          border: 1px solid #e8e8e8;
          margin-bottom: 16px;
        }

        &__title {
          margin-bottom: 0;
          padding: 8px 0 8px 15px;
        }
      }
    }

    &__summary {
      margin-top: 32px;

      .item {
        display: flex;
        justify-content: flex-end;

        &__label {
          text-align: right;
          //font-size: 16px;
          line-height: 24px;

          &--refund {
            color: #eb5757;
            margin-top: 8px;
            display: flex;
            align-items: center;
          }

          &--bold {
            font-weight: 600;
            //font-size: 16px;
            line-height: 32px;
            margin-top: 8px;
          }
        }

        &__number {
          text-align: right;
          min-width: 80px;

          /* margin-left: 60px; */
          //font-size: 16px;
          line-height: 24px;
          font-weight: 600;

          &--refund {
            color: #eb5757;
            margin-top: 8px;
          }

          &--bold {
            font-weight: 600;
            //font-size: 16px;
            line-height: 32px;
            margin-top: 8px;
          }
        }
      }

      @media screen and (max-width: 768px) {
        margin-bottom: 3rem;
      }
    }
  }
`

const CountdownStyle = css.resolve`
  .countdown {
    color: var(--secondary);
    display: inline-block;
    font-weight: bold;
    min-width: 44px;
  }
`
